import { TaskpaneReadContainer } from "../components/Taskpane/TaskpaneReadContainer";
import { TaskpaneComposeContainer } from "../components/Taskpane/TaskpaneComposeContainer";
import { getMatterRetriever } from "../services/matter-retriever";
import { showAttachFiles, showSelectFolder } from "../services/dialog-service";
import {
  AuthenticationStatus,
  SmokeballAuthenticator,
  storeModeInBrowser,
  TSmokeballTier,
} from "../services/smokeball-authenticator";
import { NotificationsContainer } from "../components/Notifications/NotificationsContainer";
import { getMatterIdPropertyName } from "../services/mailbox-service";
import { ComponentProps, useEffect, useState } from "react";
import { TMatter } from "../models/matter-model";
import { useHistory } from "react-router-dom";
import { LogoutContainer } from "../components/Logout/LogoutContainer";
import {
  associateMatterIdInConversationCache,
  getMatterIdFromConversationCache,
} from "../services/conversation-service";
import { getMatterAssigner } from "../services/assign-to-matter-service";
import { useNotificationStore } from "../components/Notifications/notification-store";
import { useItemStore } from "../mailitem/mail-item-store";
import { IMailItem } from "../mailitem/mail-item-types";
import { MessageContainer, Severity } from "../components/Message/MessageContainer";
import { ErrorType } from "../helpers/ErrorType";

export const TaskpanePage = (props: { route: "read" | "compose" | "default" }) => {
  const history = useHistory();
  const matterAssigner = getMatterAssigner();
  const matterRetriever = getMatterRetriever();
  const mailItem = useItemStore((s) => s.wrappedMailItem);
  const authenticator = SmokeballAuthenticator.getSmokeballAuthenticator();
  const errorNotification = useNotificationStore((s) => s.errorNotification);

  const [authed, setAuthed] = useState(false);
  const [tier, setTier] = useState<TSmokeballTier>(null);
  const [assignedMatter, setAssignedMatter] = useState<TMatter | null>(null);
  const [isCheckingForAssignedMatter, setIsCheckingForAssignedMatter] = useState(true);

  useEffect(() => {
    async function getAuthState() {
      const authState = await authenticator.getAuthStatus(true);

      if (authState.status !== AuthenticationStatus.LoggedIn) {
        setAuthed(false);
        console.info("Not authenticated: Pushing to /start");
        storeModeInBrowser(props.route === "compose" ? "compose" : "read");
        history.push("/start");
      } else {
        const tier = authState.token?.smokeballTier;

        setAuthed(true);
        setTier(tier ?? null);
      }
    }

    getAuthState().catch((error) => console.error(error));
  }, [history]);

  // fires on switching matters
  useEffect(() => {
    if (!authed) {
      return;
    }

    const loadMatterForMailItem = async () => {
      if (!mailItem) {
        setAssignedMatter(null);
        return;
      }

      const matter = await tryLoadMatterForMailItem(mailItem);

      setAssignedMatter(matter);

      if (props.route === "read") {
        // pretty hard to know if we should associate here if the matter
        // is falsy, but Matt thinks better to associate no matter than
        // a wrong one and the user could have manually cleared it.
        if (matter) {
          associateMatterIdInConversationCache(mailItem.conversationId, matter.matterId);
        }
      }

      if (!matter && props.route === "compose") {
        // we are in a compse view which has no matter associated
        // let's associate one from the cache
        const matterId = getMatterIdFromConversationCache(mailItem.conversationId);

        if (matterId) {
          const assignResult = await matterAssigner
            .assignDraftToMatter(mailItem, matterId)
            .catch((e) => errorNotification("Assign draft failed", e));

          if (assignResult && assignResult.err) {
            if (assignResult.err === ErrorType.Unauthorized || assignResult.err === ErrorType.NoMatterPermission) {
              errorNotification("You currently don't have permission to access this Matter");
            } else {
              errorNotification("Assign draft failed", assignResult.err);
            }
            return;
          }

          const result = await matterRetriever.getMatterById(matterId);

          if (!result.hasError) {
            setAssignedMatter(result.matter);
          } else {
            console.error(result.error);
            errorNotification(
              "Error loading matter, possible environment mismatch between read and compose panes",
              result.error
            );
          }
        }
      }
    };

    setIsCheckingForAssignedMatter(true);

    loadMatterForMailItem()
      .then(() => {
        setIsCheckingForAssignedMatter(false);
      })
      .catch((err) => {
        console.error(err);
        errorNotification("Error getting matter for mailbox item", err);

        setAssignedMatter(null);
        setIsCheckingForAssignedMatter(false);
      });
  }, [authed, mailItem]);

  if (!authed) {
    return null;
  }

  const tryLoadMatterForMailItem = async (mailItem: IMailItem | null | undefined): Promise<TMatter | null> => {
    if (!mailItem || mailItem === null) {
      return null;
    }

    console.info(`Mail item selected. Subject: ${mailItem.subject}`);
    const matterId = await mailItem.getCustomProperty(getMatterIdPropertyName());

    if (matterId && matterId !== null) {
      console.info(`Mail Item select. Subject: ${mailItem.subject}. Matter ID: ${matterId}.`);

      const result = await matterRetriever.getMatterById(matterId);

      if (!result.hasError) {
        return result.matter;
      } else {
        console.error(result.error);
        errorNotification("Error loading matter", result.error);
      }
    }

    return null;
  };

  const openAttachFiles = async (matterId: string) => {
    showAttachFiles(matterId).catch((e) => errorNotification("showAttachFiles failed to launch", e));
  };

  const openSelectFolder = async (matterId: string, cb: (folderId: string, display: string) => void) => {
    showSelectFolder(matterId, cb).catch((e) => errorNotification("showSelectFolder failed to launch", e));
  };

  return (
    <TaskpanePageInjectable
      {...{
        mailItem,
        assignedMatter,
        setAssignedMatter,
        isCheckingForAssignedMatter,
        matterRetriever,
        matterAssigner,
        openAttachFiles,
        openSelectFolder,
        authenticator,
        tier,
        searchViewType: "DEFAULT",
        route: props.route,
      }}
    />
  );
};

type InjectableProps = ComponentProps<typeof TaskpanePage> &
  ComponentProps<typeof TaskpaneComposeContainer> &
  ComponentProps<typeof TaskpaneReadContainer> &
  ComponentProps<typeof LogoutContainer>;

export const TaskpanePageInjectable = (props: InjectableProps) => {
  const mailItem = useItemStore((s) => s.wrappedMailItem);

  return (
    <div className="flex flex-col h-full bg-neutral-50">
      {props.route === "default" ? (
        <div className="my-2">
          <MessageContainer
            severity={Severity.Warning}
            title="Manifest out of date"
            content="The current manifest file is out of date."
          ></MessageContainer>
        </div>
      ) : null}

      <NotificationsContainer className="m-2" showTopN={3} />

      {mailItem ? (
        <div className="flex flex-col grow min-h-0">
          {props.route == "compose" ? (
            <TaskpaneComposeContainer
              tier={props.tier}
              authenticator={props.authenticator}
              assignedMatter={props.assignedMatter}
              matterAssigner={props.matterAssigner}
              matterRetriever={props.matterRetriever}
              openAttachFiles={props.openAttachFiles}
              setAssignedMatter={props.setAssignedMatter}
              isCheckingForAssignedMatter={props.isCheckingForAssignedMatter}
            />
          ) : (
            <TaskpaneReadContainer
              tier={props.tier}
              authenticator={props.authenticator}
              assignedMatter={props.assignedMatter}
              matterAssigner={props.matterAssigner}
              matterRetriever={props.matterRetriever}
              openSelectFolder={props.openSelectFolder}
              setAssignedMatter={props.setAssignedMatter}
              isCheckingForAssignedMatter={props.isCheckingForAssignedMatter}
            />
          )}
        </div>
      ) : (
        <div className="p-2 flex flex-col grow min-h-0">
          <MessageContainer
            severity={Severity.Information}
            title="No mail item selected"
            content="Select a mail item to view matter details or save to a matter."
          ></MessageContainer>
        </div>
      )}
    </div>
  );
};
